/**
 * What each effect type exposes, as data.
 *
 * The nine effect editors under EffectBlocks/ are modal-shaped: each owns its
 * local state, renders its own preview, and ends in a POST that bakes a new
 * asset. On the timeline an effect is a live object with no apply step, so
 * describing the controls once and rendering them generically is both less
 * code than rewriting nine panels and the only way the inspector stays
 * consistent between types.
 *
 * Field names and ranges mirror Backend/Common/AI/effect_blocks.py exactly —
 * these params are compiled there, so a name that drifts is a silently
 * ignored control.
 *
 * Spatial params (x, y, w, h) are deliberately absent: they are manipulated by
 * dragging on the stage, which is more direct than any number entry.
 */

export const EFFECT_SPECS = {
    text: {
        label: 'Text',
        defaults: {
            text: 'Your text', x: 0.5, y: 0.5, fontFrac: 0.06,
            color: '#ffffff', anchor: 'center', shadow: true, box: false,
        },
        fields: [
            { key: 'text', type: 'text', label: 'Text' },
            { key: 'fontFrac', type: 'range', label: 'Size', min: 0.02, max: 0.2, step: 0.005, format: v => `${Math.round(v * 100)}%` },
            { key: 'color', type: 'color', label: 'Colour' },
            { key: 'anchor', type: 'select', label: 'Align', options: ['left', 'center', 'right'] },
            { key: 'fontFamily', type: 'select', label: 'Font', options: ['', 'Futura Bold', 'Segoe UI Semibold', 'Impact', 'Georgia Italic'] },
            { key: 'shadow', type: 'toggle', label: 'Shadow' },
            { key: 'box', type: 'toggle', label: 'Backing box' },
        ],
    },

    box: {
        label: 'Box',
        defaults: { x: 0.25, y: 0.25, w: 0.5, h: 0.3, color: '#d9363e', filled: false, thicknessFrac: 0.006 },
        fields: [
            { key: 'color', type: 'color', label: 'Colour' },
            { key: 'filled', type: 'toggle', label: 'Filled' },
            { key: 'thicknessFrac', type: 'range', label: 'Thickness', min: 0.002, max: 0.03, step: 0.001, format: v => `${(v * 100).toFixed(1)}%` },
        ],
    },

    crop: {
        label: 'Crop',
        defaults: { x: 0.1, y: 0.1, w: 0.8, h: 0.8 },
        fields: [],
        hint: 'Drag the region on the preview to choose what to keep.',
    },

    zoom: {
        label: 'Zoom',
        defaults: { zoom: 1.5, x: 0.5, y: 0.5 },
        fields: [
            { key: 'zoom', type: 'range', label: 'Amount', min: 1.05, max: 3, step: 0.05, format: v => `${v.toFixed(2)}×` },
        ],
        hint: 'Drag the window on the preview to set what it punches in on.',
    },

    sticker: {
        label: 'Sticker',
        defaults: { x: 0.5, y: 0.5, scale: 0.25 },
        fields: [
            { key: 'overlayAsset', type: 'asset', label: 'Image', assetKind: 'image' },
            { key: 'scale', type: 'range', label: 'Size', min: 0.05, max: 0.9, step: 0.01, format: v => `${Math.round(v * 100)}%` },
        ],
    },

    overlay: {
        label: 'Graphic',
        defaults: { x: 0.5, y: 0.35, scale: 0.6, animate: true, animDuration: 0.4, label: '' },
        fields: [
            { key: 'label', type: 'text', label: 'Label' },
            { key: 'scale', type: 'range', label: 'Size', min: 0.2, max: 1, step: 0.01, format: v => `${Math.round(v * 100)}%` },
            { key: 'animate', type: 'toggle', label: 'Animate in' },
            { key: 'animDuration', type: 'range', label: 'Animation', min: 0.1, max: 1.5, step: 0.05, format: v => `${v.toFixed(2)}s` },
        ],
    },

    fade: {
        label: 'Fade',
        defaults: { fadeType: 'in', duration: 1 },
        fields: [
            { key: 'fadeType', type: 'select', label: 'Direction', options: ['in', 'out'] },
            { key: 'duration', type: 'range', label: 'Length', min: 0.2, max: 4, step: 0.1, format: v => `${v.toFixed(1)}s` },
        ],
    },

    flip: {
        label: 'Flip',
        defaults: { direction: 'horizontal' },
        fields: [
            { key: 'direction', type: 'select', label: 'Axis', options: ['horizontal', 'vertical'] },
        ],
    },

    look: {
        label: 'Colour',
        defaults: { warmth: 0, saturation: 1, brightness: 0, grayscale: false },
        fields: [
            { key: 'warmth', type: 'range', label: 'Warmth', min: -1, max: 1, step: 0.05, format: v => v.toFixed(2) },
            { key: 'saturation', type: 'range', label: 'Saturation', min: 0, max: 2, step: 0.05, format: v => v.toFixed(2) },
            { key: 'brightness', type: 'range', label: 'Brightness', min: -0.3, max: 0.3, step: 0.01, format: v => v.toFixed(2) },
            { key: 'grayscale', type: 'toggle', label: 'Black & white' },
        ],
    },
};

/** The order effects are offered in: the ones reached for most, first. */
export const EFFECT_MENU = ['text', 'zoom', 'crop', 'box', 'sticker', 'overlay', 'fade', 'flip', 'look'];
